// 카메라 충돌·가림 판정(CPU, 프레임 루프 할당 없음). 2026-11-04 카메라 가림 레인.
//
// 하이엔드 ARPG 방식 두 갈래를 합친다.
//  - 관통(카메라가 상점·성문·큰 바위 안에 들어감): 그 물체의 바깥면 앞까지 카메라 팔을 당긴다(스프링 암). 당김은 빠르게, 복귀는 느리게.
//  - 가림(카메라는 밖인데 물체가 영웅을 덮음): 카메라를 당기지 않고 그 물체만 디더로 비친다(셰이더 칸 setOccluderFade).
// 카메라 쪽 벽은 원래 컷어웨이(cut.ts)가 낮추므로, 컷·디더 뒤에 '실제로 보이는' 높이만 막는 것으로 친다.
//
// 대상: 장면의 키트·세트·마을 인스턴스 메시(aCut 속성이 있는 InstancedMesh, 컬링 뒤 활성 인스턴스)와 그레이박스 벽(aCut vec3).
// 각 인스턴스는 지오메트리 경계 상자를 인스턴스 행렬로 옮긴 월드 축 정렬 상자(AABB)로 근사한다.
import * as THREE from 'three/webgpu';
import { CUT_H, cutFactor, kitDitherFactor } from './env/cut';

/** 영웅 둘레 이 반경(m, 수평) 안의 인스턴스만 상자로 만든다(카메라 최대 수평 거리 + 여유) */
const GATHER_R = 34;
const MAX_BOXES = 768;
/** 상자당 실수 개수: minX minY minZ maxX maxY maxZ cx cz mode(0..2, 벽 3) */
const STRIDE = 9;
/** 삼각형이 이보다 적은 인스턴스(풀·꽃 판)는 막지 않는다 */
const MIN_TRI = 9;
/** 스프링 암 뿌리 높이(m): 카메라 목표점(바닥)에서 영웅 가슴 높이로 올린다 */
export const ROOT_UP = 1.2;
/** 원하는 팔 길이의 이 비율보다 영웅 쪽에서 닿는 물체는 당기지 않고 비친다 */
export const NEAR_FRAC = 0.45;

interface Source {
  mesh: THREE.InstancedMesh;
  /** 지오메트리 경계 상자 가운데·반폭 */
  c: Float32Array;
  h: Float32Array;
  /** aCut 항목 크기(4 = 키트 x,z,방식,y배율 · 3 = 그레이박스 벽 x,z,허용) */
  item: number;
}

export interface CameraSolve {
  /** 관통을 피하려면 카메라 팔(영웅 쪽 목표 → 카메라 거리)이 이 길이 이하여야 한다(Infinity = 제한 없음) */
  maxArm: number;
  /** 당겨도 못 피한(최소 팔 길이 아래) 관통 상자 수 — 이 상자들은 가림 칸으로 비친다 */
  unresolved: number;
  /** 가림 물체 수(칸에 넣을 후보, 최대 OCC_MAX) */
  occN: number;
  /** 가림 물체 중심 x,z와 우선순위(영웅에 가까울수록 큼) */
  occ: Float32Array;
}

export const OCC_MAX = 4;

export class CameraCollider {
  private sources: Source[] = [];
  readonly boxes = new Float32Array(MAX_BOXES * STRIDE);
  boxN = 0;
  private readonly res: CameraSolve = { maxArm: Infinity, unresolved: 0, occN: 0, occ: new Float32Array(OCC_MAX * 3) };
  private readonly m = new Float32Array(16);

  /** 장면이 바뀔 때(맵 로드·환경 준비) 대상 메시를 다시 모은다 */
  setRoots(roots: readonly THREE.Object3D[]): void {
    this.sources.length = 0;
    const walk = (o: THREE.Object3D): void => {
      const im = o as THREE.InstancedMesh;
      if (im.isInstancedMesh && im.geometry) {
        const g = im.geometry;
        const a = g.getAttribute('aCut');
        const mat = im.material as THREE.Material;
        const tri = g.index ? g.index.count / 3 : (g.getAttribute('position')?.count ?? 0) / 3;
        if (a && (a.itemSize === 3 || a.itemSize === 4) && mat && !mat.transparent && !(im.userData && im.userData.decor === true) && tri >= MIN_TRI) {
          if (!g.boundingBox) g.computeBoundingBox();
          const b = g.boundingBox!;
          this.sources.push({
            mesh: im,
            c: new Float32Array([(b.min.x + b.max.x) / 2, (b.min.y + b.max.y) / 2, (b.min.z + b.max.z) / 2]),
            h: new Float32Array([(b.max.x - b.min.x) / 2, (b.max.y - b.min.y) / 2, (b.max.z - b.min.z) / 2]),
            item: a.itemSize,
          });
        }
      }
      for (const c of o.children) walk(c);
    };
    for (const r of roots) walk(r);
  }

  get sourceCount(): number { return this.sources.length; }

  /** 영웅 둘레 활성 인스턴스를 상자로 모은다(매 프레임 또는 몇 프레임마다) */
  gather(hx: number, hz: number): void {
    const B = this.boxes, M = this.m;
    let n = 0;
    for (const s of this.sources) {
      const mesh = s.mesh;
      if (!mesh.visible || !visibleChain(mesh)) continue;
      const cnt = mesh.count;
      const mat = mesh.instanceMatrix.array as Float32Array;
      const cut = mesh.geometry.getAttribute('aCut').array as Float32Array;
      const w = mesh.matrixWorld.elements;
      const ident = isIdentity(w);
      for (let i = 0; i < cnt && n < MAX_BOXES; i++) {
        const o = i * 16;
        // 인스턴스 행렬(필요하면 메시 월드 행렬을 곱한다)
        if (ident) { for (let j = 0; j < 16; j++) M[j] = mat[o + j]; }
        else mul4(w, mat, o, M);
        const tx = M[12], tz = M[14];
        if (Math.abs(tx - hx) > GATHER_R || Math.abs(tz - hz) > GATHER_R) continue;
        const cx0 = s.c[0], cy0 = s.c[1], cz0 = s.c[2], ex = s.h[0], ey = s.h[1], ez = s.h[2];
        // 가운데 = M·c, 반폭 = |M3x3|·h
        const wx = M[0] * cx0 + M[4] * cy0 + M[8] * cz0 + M[12];
        const wy = M[1] * cx0 + M[5] * cy0 + M[9] * cz0 + M[13];
        const wz = M[2] * cx0 + M[6] * cy0 + M[10] * cz0 + M[14];
        const hxw = Math.abs(M[0]) * ex + Math.abs(M[4]) * ey + Math.abs(M[8]) * ez;
        const hyw = Math.abs(M[1]) * ex + Math.abs(M[5]) * ey + Math.abs(M[9]) * ez;
        const hzw = Math.abs(M[2]) * ex + Math.abs(M[6]) * ey + Math.abs(M[10]) * ez;
        if (!(hxw > 0.05 && hyw > 0.05 && hzw > 0.05) || !Number.isFinite(wx + wy + wz)) continue;
        // 바닥 판(높이 30cm 이하)은 막지 않는다
        if (wy + hyw < 0.3) continue;
        const b = n * STRIDE;
        B[b] = wx - hxw; B[b + 1] = wy - hyw; B[b + 2] = wz - hzw;
        B[b + 3] = wx + hxw; B[b + 4] = wy + hyw; B[b + 5] = wz + hzw;
        if (s.item === 4) { B[b + 6] = cut[i * 4]; B[b + 7] = cut[i * 4 + 1]; B[b + 8] = cut[i * 4 + 2]; }
        else { B[b + 6] = cut[i * 3]; B[b + 7] = cut[i * 3 + 1]; B[b + 8] = cut[i * 3 + 2] > 0 ? 3 : 0; }
        n++;
      }
    }
    this.boxN = n;
  }

  /** 상자 i의 '보이는' 윗면 높이(컷어웨이 반영). -Infinity면 이번 프레임엔 막지 않는다(디더로 비침) */
  visibleTop(i: number, hx: number, hz: number): number {
    const B = this.boxes, b = i * STRIDE, mode = B[b + 8], top = B[b + 4];
    if (mode === 0) return top;
    const cx = B[b + 6], cz = B[b + 7];
    if (mode === 3) return cutFactor(cx, cz, hx, hz) > 0.5 ? Math.min(top, CUT_H) : top;
    if (mode === 1) { const k = cutFactor(cx, cz, hx, hz); return k < 0.002 ? top : Math.min(top, 7.5 - 6.9 * k); }
    if (mode === 2) return kitDitherFactor(cx, cz) > 0.5 ? -Infinity : top;
    return top;
  }

  /**
   * 카메라 위치를 판정한다.
   * @param ax..az 카메라 팔의 뿌리(카메라가 바라보는 목표점)
   * @param ux..uz 뿌리 → 카메라 단위 방향
   * @param arm 원하는 팔 길이(m)
   * @param hx,hz 영웅 수평 위치(가림 시선의 끝·컷 기준)
   * @param minArm 이보다 짧게는 당기지 않는다(그 아래 관통은 비침으로 처리)
   * @param pad 카메라 둘레 여유(m, 근평면·구 반경)
   */
  solve(ax: number, ay: number, az: number, ux: number, uy: number, uz: number, arm: number, hx: number, hy: number, hz: number, minArm: number, pad: number): CameraSolve {
    const r = this.res, B = this.boxes;
    r.maxArm = Infinity; r.unresolved = 0; r.occN = 0;
    // 스프링 암: 영웅 가슴 높이(뿌리 + ROOT_UP)에서 원하는 카메라 위치까지 선분을 쏴 처음 닿는 '보이는' 상자 앞에서 멈춘다.
    // 뿌리를 바닥(목표점 y)에 두면 영웅 발치의 낮은 소품(우물·화단)이 모두 걸려 카메라가 영웅 쪽으로 쏠렸다(2026-11-04 1차 측정).
    const cxp = ax + ux * arm, cyp = ay + uy * arm, czp = az + uz * arm;
    const rx = ax, ry = ay + ROOT_UP, rz = az;
    let dx = cxp - rx, dy = cyp - ry, dz = czp - rz;
    const L = Math.hypot(dx, dy, dz);
    if (L > 1e-6) { dx /= L; dy /= L; dz /= L; }
    for (let i = 0; i < this.boxN; i++) {
      const b = i * STRIDE;
      const top = this.visibleTop(i, hx, hz);
      if (top === -Infinity) continue;
      const x0 = B[b], y0 = B[b + 1], z0 = B[b + 2], x1 = B[b + 3], z1 = B[b + 5];
      const y1 = top;
      if (y1 <= y0 || L <= 1e-6) continue;
      const tIn = slabEnter(rx, ry, rz, dx, dy, dz, x0 - pad, y0 - pad, z0 - pad, x1 + pad, y1 + pad, z1 + pad);
      if (!(tIn < L)) continue;
      const camIn = cxp > x0 - pad && cxp < x1 + pad && cyp > y0 - pad && cyp < y1 + pad && czp > z0 - pad && czp < z1 + pad;
      // 팔 길이로 환산(뿌리가 ROOT_UP 위라 비율로 옮긴다)
      const armHit = (tIn / L) * arm;
      if (tIn <= 1e-6) { if (camIn) { r.unresolved++; this.pushOcc(B[b + 6], B[b + 7], 2, B[b + 8]); } continue; } // 영웅이 상자(아치 통로 등) 안
      if (armHit >= minArm && armHit >= arm * NEAR_FRAC) { if (armHit < r.maxArm) r.maxArm = armHit; }
      else {
        // 영웅 코앞 물체: 당기면 카메라가 영웅에 붙으므로 비친다
        if (camIn) r.unresolved++;
        this.pushOcc(B[b + 6], B[b + 7], camIn ? 2 : 1.5, B[b + 8]);
      }
    }
    // 가림: 실제로 쓸(당긴) 카메라 위치 → 영웅 몸(발목·가슴·머리) 시선 세 줄 중 둘 이상을 막는 물체를 비친다
    const armUse = Math.min(arm, r.maxArm);
    const qx = ax + ux * armUse, qy = ay + uy * armUse, qz = az + uz * armUse;
    for (let i = 0; i < this.boxN; i++) {
      const b = i * STRIDE;
      const top = this.visibleTop(i, hx, hz);
      if (top === -Infinity) continue;
      const x0 = B[b], y0 = B[b + 1], z0 = B[b + 2], x1 = B[b + 3], z1 = B[b + 5];
      if (top <= y0) continue;
      let hit = 0;
      for (let k = 0; k < 3; k++) {
        const py = hy + (k === 0 ? 0.35 : k === 1 ? 1.15 : 1.75);
        if (segHitsBox(qx, qy, qz, hx, py, hz, x0, y0, z0, x1, top, z1)) hit++;
      }
      if (hit >= 2) {
        // 영웅에 가까운 가림체를 먼저(우선순위 = 1/거리)
        const d = Math.hypot((x0 + x1) / 2 - hx, (z0 + z1) / 2 - hz);
        this.pushOcc(B[b + 6], B[b + 7], 1 / (1 + d), B[b + 8]);
      }
    }
    return r;
  }

  private pushOcc(cx: number, cz: number, pri: number, mode: number): void {
    if (mode === 3) return; // 그레이박스 벽은 칸 비침을 지원하지 않는다(컷어웨이가 처리)
    const r = this.res, o = r.occ;
    for (let j = 0; j < r.occN; j++) if (Math.abs(o[j * 3] - cx) < 0.05 && Math.abs(o[j * 3 + 1] - cz) < 0.05) { if (pri > o[j * 3 + 2]) o[j * 3 + 2] = pri; return; }
    if (r.occN < OCC_MAX) { const j = r.occN++; o[j * 3] = cx; o[j * 3 + 1] = cz; o[j * 3 + 2] = pri; return; }
    // 꽉 차면 가장 낮은 우선순위를 바꾼다
    let lo = 0;
    for (let j = 1; j < OCC_MAX; j++) if (o[j * 3 + 2] < o[lo * 3 + 2]) lo = j;
    if (pri > o[lo * 3 + 2]) { o[lo * 3] = cx; o[lo * 3 + 1] = cz; o[lo * 3 + 2] = pri; }
  }
}

function visibleChain(o: THREE.Object3D): boolean {
  for (let p: THREE.Object3D | null = o; p; p = p.parent) if (!p.visible) return false;
  return true;
}

function isIdentity(e: ArrayLike<number>): boolean {
  return e[0] === 1 && e[5] === 1 && e[10] === 1 && e[15] === 1 && e[1] === 0 && e[2] === 0 && e[4] === 0 && e[6] === 0 && e[8] === 0 && e[9] === 0 && e[12] === 0 && e[13] === 0 && e[14] === 0;
}

/** out = a(4x4 열 우선) × b[o..o+16] */
function mul4(a: ArrayLike<number>, b: Float32Array, o: number, out: Float32Array): void {
  for (let c = 0; c < 4; c++) {
    const b0 = b[o + c * 4], b1 = b[o + c * 4 + 1], b2 = b[o + c * 4 + 2], b3 = b[o + c * 4 + 3];
    for (let r = 0; r < 4; r++) out[c * 4 + r] = a[r] * b0 + a[4 + r] * b1 + a[8 + r] * b2 + a[12 + r] * b3;
  }
}

/** 광선(o + d·t, t ≥ 0)이 상자에 들어가는 t. 원점이 이미 안이면 0, 안 만나면 Infinity */
export function slabEnter(ox: number, oy: number, oz: number, dx: number, dy: number, dz: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): number {
  let tmin = 0, tmax = Infinity;
  // 축별(할당을 피하려고 펼친다)
  for (let k = 0; k < 3; k++) {
    const o = k === 0 ? ox : k === 1 ? oy : oz;
    const d = k === 0 ? dx : k === 1 ? dy : dz;
    const lo = k === 0 ? x0 : k === 1 ? y0 : z0;
    const hi = k === 0 ? x1 : k === 1 ? y1 : z1;
    if (Math.abs(d) < 1e-9) { if (o < lo || o > hi) return Infinity; continue; }
    let t1 = (lo - o) / d, t2 = (hi - o) / d;
    if (t1 > t2) { const t = t1; t1 = t2; t2 = t; }
    if (t1 > tmin) tmin = t1;
    if (t2 < tmax) tmax = t2;
    if (tmin > tmax) return Infinity;
  }
  return tmin;
}

/** 선분 p→q가 상자를 지나는지(양 끝 30cm는 빼서 카메라·영웅 몸에 붙은 면은 세지 않는다) */
export function segHitsBox(px: number, py: number, pz: number, qx: number, qy: number, qz: number, x0: number, y0: number, z0: number, x1: number, y1: number, z1: number): boolean {
  const dx = qx - px, dy = qy - py, dz = qz - pz;
  const len = Math.hypot(dx, dy, dz);
  if (len < 0.7) return false;
  const t = slabEnter(px, py, pz, dx / len, dy / len, dz / len, x0, y0, z0, x1, y1, z1);
  return t < len - 0.3 && slabEnter(qx, qy, qz, -dx / len, -dy / len, -dz / len, x0, y0, z0, x1, y1, z1) < len - 0.3;
}

/**
 * 문짝(축 정렬 사각형 x0..x1, z0..z1) 가림 정도 0..1: 카메라→영웅 수평 선분이 문 사각형(+0.3m)을 지나고 영웅이 문 가까이(1.5m 안 1, 3m에서 0) 있을 때.
 * 렌더가 문 높이를 컷어웨이처럼 낮추는 데 쓴다(문 메시는 키트 인스턴스가 아니라 가림 칸 디더를 쓸 수 없다).
 */
export function gateOcclusion(x0: number, z0: number, x1: number, z1: number, cx: number, cz: number, hx: number, hz: number): number {
  const pad = 0.3;
  const ax0 = x0 - pad, az0 = z0 - pad, ax1 = x1 + pad, az1 = z1 + pad;
  const dx = Math.max(ax0 - hx, 0, hx - ax1), dz = Math.max(az0 - hz, 0, hz - az1);
  const near = Math.hypot(dx, dz);
  if (near >= 3) return 0;
  const sx = hx - cx, sz = hz - cz;
  const len = Math.hypot(sx, sz);
  if (len < 1e-6) return 0;
  const t = slabEnter(cx, 0, cz, sx / len, 0, sz / len, ax0, -1, az0, ax1, 1, az1);
  if (!(t < len)) return 0;
  const w = Math.min(1, Math.max(0, (near - 1.5) / 1.5));
  return 1 - w * w * (3 - 2 * w);
}
